import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChangeCircleOutlinedIcon from '@mui/icons-material/ChangeCircleOutlined';
import HubIcon from '@mui/icons-material/Hub';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyState, PageLoadingSpinner } from '../../components/ui';
import { useDocumentTitle } from '../../hooks';
import { ApiError } from '../../lib/api';
import {
  fetchPortfolioSummary,
  fetchGraphMonitoringRules,
} from './monitoringApi';
import { PortfolioDiff } from './portfolioDiffModel';
import { GraphMonitoringRuleCatalogEntry } from './types';
import { formatDateTime, formatRelativeTime, humanizeValue } from './utils';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/i18n';
import { getMonitoringRuleName } from '../../i18n/backendTranslations';

function SummaryMetricCard({
  icon,
  label,
  value,
  helper,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  helper: string;
}) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 'var(--radius-xl)',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'var(--radius-lg)',
            bgcolor: 'action.hover',
            color: 'primary.main',
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {helper}
      </Typography>
    </Paper>
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (typeof error.body === 'string' && error.body.trim()) {
      return error.body;
    }

    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return i18n.t('monitoring.portfolio.loadFailed');
}

function formatCompactCounts(changed: number, added: number, removed: number) {
  return i18n.t('monitoring.portfolio.compactCounts', { changed, added, removed });
}

export default function GraphMonitoringPortfolioDiff() {
  const { t, i18n: currentI18n } = useTranslation();
  const [portfolioDiff, setPortfolioDiff] = useState<PortfolioDiff | null>(null);
  const [availableRules, setAvailableRules] = useState<GraphMonitoringRuleCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rulesError, setRulesError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const deferredSearchTerm = useDeferredValue(searchTerm.trim().toLowerCase());
  const requestRef = useRef(0);

  useDocumentTitle(`${t('monitoring.portfolioTitle')} — Breadify`);

  const fetchPage = useCallback(async (mode: 'initial' | 'refresh' = 'initial', currentPage = page, currentRowsPerPage = rowsPerPage) => {
    const requestId = ++requestRef.current;

    if (mode === 'initial') {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    setError(null);
    setRulesError(null);

    try {
      const [portfolioResult, rulesResult] = await Promise.allSettled([
        fetchPortfolioSummary(currentPage, currentRowsPerPage),
        fetchGraphMonitoringRules(),
      ]);

      if (requestId !== requestRef.current) {
        return;
      }

      if (rulesResult.status === 'fulfilled') {
        setAvailableRules(rulesResult.value);
      } else {
        setAvailableRules([]);
        setRulesError(getErrorMessage(rulesResult.reason));
      }

      if (portfolioResult.status === 'fulfilled') {
        setPortfolioDiff(portfolioResult.value);
        setLastLoadedAt(new Date());
      } else {
        setPortfolioDiff(null);
        setError(getErrorMessage(portfolioResult.reason));
      }
    } catch (fetchError) {
      if (requestId === requestRef.current) {
        setPortfolioDiff(null);
        setError(getErrorMessage(fetchError));
      }
    } finally {
      if (requestId === requestRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchPage('initial', page, rowsPerPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const ruleLookup = useMemo(
    () => new Map(availableRules.map((rule) => [rule.ruleKey, rule.displayName])),
    [availableRules],
  );

  const filteredCompanies = useMemo(() => {
    const companies = portfolioDiff?.companies ?? [];
    if (!deferredSearchTerm) {
      return companies;
    }

    return companies.filter((company) =>
      [
        company.displayName,
        company.orgNumber,
        ...company.ruleMatches,
      ]
        .join(' ')
        .toLowerCase()
        .includes(deferredSearchTerm),
    );
  }, [portfolioDiff?.companies, deferredSearchTerm]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <PageLoadingSpinner label={t('monitoring.portfolio.loading')} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Breadcrumbs aria-label={t('monitoring.detail.breadcrumb')}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/graphmonitoring">
          {t('monitoring.overviewTitle')}
        </Link>
        <Typography color="text.primary">{t('monitoring.portfolioTitle')}</Typography>
      </Breadcrumbs>

      <Paper
        sx={{
          p: 3,
          borderRadius: 'var(--radius-xl)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.75 }}>
              {t('monitoring.portfolioTitle')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 820 }}>
              {t('monitoring.portfolio.description')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
              <Chip label={t('monitoring.portfolio.monitoredCompanies', { count: portfolioDiff?.totalCompanies ?? 0 })} size="small" />
              <Chip
                label={t('monitoring.portfolio.companiesWithChanges', { count: portfolioDiff?.companiesWithDiffs ?? 0 })}
                size="small"
                variant="outlined"
              />
              {lastLoadedAt && (
                <Chip
                  label={t('monitoring.updatedAt', { time: lastLoadedAt.toLocaleTimeString(currentI18n.resolvedLanguage === 'sv' ? 'sv-SE' : 'en-SE', {
                    hour: '2-digit',
                    minute: '2-digit',
                  }) })}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/graphmonitoring"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
            >
              {t('common.actions.back')}
            </Button>
            <Button
              variant="contained"
              startIcon={refreshing ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              disabled={refreshing}
              onClick={() => {
                setPage(0);
                fetchPage('refresh', 0, rowsPerPage);
              }}
            >
              {refreshing ? t('monitoring.overview.refreshing') : t('common.actions.refresh')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      {rulesError && (
        <Alert severity="warning">
          {t('monitoring.portfolio.rulesUnavailable')}
        </Alert>
      )}

      {!portfolioDiff?.totalCompanies ? (
        <Paper
          sx={{
            borderRadius: 'var(--radius-xl)',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <EmptyState
            icon={<HubIcon />}
            title={t('monitoring.portfolio.noCompanies')}
            description={t('monitoring.portfolio.noCompaniesHelp')}
            action={
              <Button
                component={RouterLink}
                to="/graphmonitoring"
                variant="contained"
                startIcon={<ArrowBackIcon />}
              >
                {t('monitoring.portfolio.goToMonitoring')}
              </Button>
            }
          />
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                xl: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 2,
            }}
          >
            <SummaryMetricCard
              icon={<HubIcon />}
              label={t('monitoring.portfolio.companiesMonitored')}
              value={String(portfolioDiff?.totalCompanies ?? 0)}
              helper={
                (portfolioDiff?.totalCompanies ?? 0) - (portfolioDiff?.companiesWithDiffs ?? 0) > 0
                  ? t('monitoring.portfolio.withoutChanges', { count: (portfolioDiff?.totalCompanies ?? 0) - (portfolioDiff?.companiesWithDiffs ?? 0) })
                  : t('monitoring.portfolio.allHaveChanges')
              }
            />
            <SummaryMetricCard
              icon={<ChangeCircleOutlinedIcon />}
              label={t('monitoring.portfolio.companiesWithChangesLabel')}
              value={String(portfolioDiff?.companiesWithDiffs ?? 0)}
              helper={t('monitoring.portfolio.latestChangeHelp')}
            />
          </Box>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 'var(--radius-xl)',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: { xs: 'stretch', md: 'flex-end' },
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('monitoring.portfolio.lastChangeByCompany')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('monitoring.portfolio.tableHelp')}
                  </Typography>
                  <Tooltip title={t('monitoring.portfolio.compactLegend')}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'text.secondary',
                        cursor: 'help',
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {t('monitoring.portfolio.compactHeadings')}
                      </Typography>
                      <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Tooltip>
                </Box>
              </Box>

              <TextField
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                label={t('monitoring.portfolio.searchCompanies')}
                placeholder={t('monitoring.portfolio.searchPlaceholder')}
                sx={{ width: { xs: '100%', md: 360 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {filteredCompanies.length === 0 ? (
              <EmptyState
                icon={<ChangeCircleOutlinedIcon />}
                title={t('monitoring.overview.noMatchingCompanies')}
                description={t('monitoring.overview.noMatchingCompaniesHelp')}
                sx={{ py: 6 }}
              />
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}
              >
                <Table size="small" aria-label={t('monitoring.portfolio.tableLabel')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('monitoring.overview.company')}</TableCell>
                      <TableCell>{t('monitoring.portfolio.latestDetected')}</TableCell>
                      <TableCell>{t('monitoring.portfolio.changes')}</TableCell>
                      <TableCell>{t('monitoring.portfolio.nodes')}</TableCell>
                      <TableCell>{t('monitoring.portfolio.relationships')}</TableCell>
                      <TableCell>{t('monitoring.portfolio.matchedRules')}</TableCell>
                      <TableCell align="right">{t('monitoring.portfolio.open')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCompanies.map((company) => (
                      <TableRow key={`${company.orgNumber}-${company.detectedAt}`} hover>
                        <TableCell sx={{ py: 1.25 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                            <Link
                              component={RouterLink}
                              to={`/graphmonitoring/${company.orgNumber}`}
                              underline="hover"
                              sx={{ fontWeight: 600 }}
                            >
                              {company.displayName}
                            </Link>
                            <Typography variant="caption" color="text.secondary">
                              {company.orgNumber}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          {company.detectedAt ? (
                            <>
                              <Typography variant="body2">
                                {formatDateTime(company.detectedAt)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatRelativeTime(company.detectedAt)}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {t('monitoring.portfolio.noChangesYet')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Chip label={t('monitoring.portfolio.changeCount', { count: company.changeCount })} size="small" />
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          {company.detectedAt ? (
                            <Typography variant="body2">
                              {formatCompactCounts(
                                company.counts.changedNodes,
                                company.counts.addedNodes,
                                company.counts.removedNodes,
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          {company.detectedAt ? (
                            <Typography variant="body2">
                              {formatCompactCounts(
                                company.counts.changedRelationships,
                                company.counts.addedRelationships,
                                company.counts.removedRelationships,
                              )}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 1.25 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {company.ruleMatches.length ? (
                              company.ruleMatches.map((ruleKey) => (
                                <Chip
                                  key={`${company.orgNumber}-${ruleKey}`}
                                  label={getMonitoringRuleName(ruleKey, ruleLookup.get(ruleKey) ?? humanizeValue(ruleKey))}
                                  size="small"
                                  variant="outlined"
                                />
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {t('monitoring.portfolio.noMatchedRules')}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25 }}>
                          <Button
                            component={RouterLink}
                            to={`/graphmonitoring/${company.orgNumber}`}
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                          >
                            {t('monitoring.portfolio.open')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <TablePagination
              component="div"
              count={portfolioDiff?.totalCompanies ?? 0}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              sx={{
                '& .MuiToolbar-root': { alignItems: 'center' },
                '& .MuiTablePagination-selectLabel': { mb: 0 },
                '& .MuiTablePagination-displayedRows': { mb: 0 },
                '& .MuiInputBase-root': { mt: 0 },
              }}
            />
          </Paper>
        </>
      )}
    </Box>
  );
}
